import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, User, X, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '918530352444';
const DISMISSED_KEY = 'spiracleLeadPopupDismissed';
const SUBMITTED_KEY = 'spiracleLeadPopupSubmitted';
const ELIGIBLE_PATHS = ['/', '/all-services'];

const LeadPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    email: '',
    address: ''
  });
  const [errors, setErrors] = useState({
    email: false,
    number: false
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const mobileRegex = /^[6-9]\d{9}$/;
  const isEligiblePage = ELIGIBLE_PATHS.includes(pathname);

  useEffect(() => {
    if (!isEligiblePage) {
      setOpen(false);
      return;
    }

    const showPopup = () => {
      if (sessionStorage.getItem(SUBMITTED_KEY) === 'true') {
        return;
      }
      setOpen(true);
    };

    const handleExitIntent = (event) => {
      if (event.clientY > 0) {
        return;
      }
      if (event.relatedTarget || event.toElement) {
        return;
      }
      showPopup();
    };

    document.addEventListener('mouseout', handleExitIntent);

    let timer;
    if (
      sessionStorage.getItem(DISMISSED_KEY) !== 'true' &&
      sessionStorage.getItem(SUBMITTED_KEY) !== 'true'
    ) {
      timer = setTimeout(showPopup, 30000);
    }

    return () => {
      document.removeEventListener('mouseout', handleExitIntent);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isEligiblePage]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const closePopup = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'number' && value.length > 10) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      setErrors((prev) => ({ ...prev, email: value ? !emailRegex.test(value) : false }));
    }

    if (name === 'number') {
      setErrors((prev) => ({ ...prev, number: value ? !mobileRegex.test(value) : false }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const emailInvalid = !emailRegex.test(formData.email);
    const numberInvalid = !mobileRegex.test(formData.number);

    setErrors({
      email: emailInvalid,
      number: numberInvalid
    });

    if (
      !formData.name.trim() ||
      !formData.address.trim() ||
      emailInvalid ||
      numberInvalid
    ) {
      return;
    }

    const message = [
      'Hello Spiracle Pest Worlds,',
      'I would like to request a pest control service.',
      '',
      `Name: ${formData.name.trim()}`,
      `Number: ${formData.number.trim()}`,
      `Email ID: ${formData.email.trim()}`,
      `Address / Location: ${formData.address.trim()}`
    ].join('\n');

    sessionStorage.setItem(SUBMITTED_KEY, 'true');
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setOpen(false);
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (!open || !isEligiblePage) {
    return null;
  }

  return (
    <div className="lead-popup-overlay" onClick={closePopup}>
      <div
        className="lead-popup"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-popup-title"
      >
        <button className="lead-popup-close" onClick={closePopup} aria-label="Close">
          <X size={20} />
        </button>

        <h2 id="lead-popup-title">Get a Free Inspection</h2>
        <p className="lead-popup-subtitle">
          Share your details and we will connect with you on WhatsApp.
        </p>

        <form className="lead-popup-form" onSubmit={handleSubmit}>
          <div className="lead-popup-group">
            <label htmlFor="lead-name">Name</label>
            <div className="lead-popup-input">
              <User size={18} />
              <input
                type="text"
                id="lead-name"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="lead-popup-group">
            <label htmlFor="lead-number">Number</label>
            <div className={`lead-popup-input ${errors.number ? 'input-error' : ''}`}>
              <Phone size={18} />
              <input
                type="tel"
                id="lead-number"
                name="number"
                placeholder="10-digit mobile number"
                value={formData.number}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="lead-popup-group">
            <label htmlFor="lead-email">Email ID</label>
            <div className={`lead-popup-input ${errors.email ? 'input-error' : ''}`}>
              <Mail size={18} />
              <input
                type="email"
                id="lead-email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="lead-popup-group">
            <label htmlFor="lead-address">Address or Location</label>
            <div className="lead-popup-input">
              <MapPin size={18} />
              <input
                type="text"
                id="lead-address"
                name="address"
                placeholder="Your address or location"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <button type="submit" className="lead-popup-submit">
            <MessageCircle size={18} />
            Submit on WhatsApp
          </button>
        </form>
      </div>
    </div>
  );
};

export default LeadPopup;
